import { EpticaExposedRequestForDraft, IEpticaDeprecatedService } from '@agent/core/channel-digit/mail/eptica/eptica-deprecated.service';
import { ToastDeprecatedService } from '@commons/core/notification/toast-deprecated.service';
import { AppExternalService } from '@commons/core/ui/app-external.service';
import { IEpticaExctractAttachment, IEpticaExctractEvent } from '@commons/history/history-mail/models/eptica-extract';
import { EpticaSelector } from '@commons/history/history-mail/models/eptica-selector';
import { ILogService, IPromise } from 'angular';
import { i18n } from 'i18next';
import { BehaviorSubject, distinctUntilChanged, Observable, Subject } from 'rxjs';

import {
  ChannelDirection,
  EpticaEnterpriseAgentExposed,
  EpticaExposedCustomer,
  EpticaExposedRequest,
  fTruthy,
  Option,
} from '@contact/upgrade';

import { EMAIL_LIST_REGEXP, EpticaHelper } from '../eptica.helper';
import { EpticaButtonActionTypes, EpticaData } from './eptica-data';

export const EPTICA_LOGIN_FRAME_NAME = 'epticaLogin';

export const EPTICA_REQUEST_FRAME_PEFIX = 'epticaRequest';
export const EPTICA_OUTGOING_FRAME_PEFIX = 'epticaOutgoing';
export const LOGIN_PAGE_NAME = 'LOGIN_PAGE';

export const REQUEST_PAGE_NAME = 'REQUEST_PAGE';
export const REQUEST_CREATE_PAGE_NAME = 'REQUEST_CREATE_PAGE';
export const HOME_PAGE_NAME = 'HOME_PAGE';
export const ACCOUNT_LIST_POPUP_PAGE_NAME = 'ACCOUNT_POPUP_LIST_PAGE';
export const ACCOUNT_GROUP_POPUP_PAGE_NAME = 'ACCOUNT_GROUP_POPUP_PAGE';
export const ACCOUNT_GROUP_POPUP_LIST_PAGE_NAME = 'ACCOUNT_GROUP_POPUP_LIST_PAGE';

export type CustomerIdType = 'es' | 'ias';

export interface RecipientFields {
  to?: string[];
  cc?: string[];
  bcc?: string[];
  valid?: boolean;
}

export interface UpdateRecipientFields {
  oldEmail?: string;
  newEmail?: string;
}

export enum EpticaRequestType {
  ASSIGNABLE = 'ASSIGNABLE',
  RELATED = 'RELATED',
  RELATED_FOR_SUP = 'RELATED_FOR_SUP',
  RELATED_FOR_SUP_CSR = 'RELATED_FOR_SUP_CSR',
  APPROVAL = 'APPROVAL',
  APPROVAL_IN_PROCESS = 'APPROVAL_IN_PROCESS',
  APPROVAL_REJECTED = 'APPROVAL_REJECTED',
  ASK_FOR_HELP_SENT = 'ASK_FOR_HELP_SENT',
  ASK_FOR_HELP_RECEIVED = 'ASK_FOR_HELP_RECEIVED',
  ASK_FOR_HELP_ANSWERED = 'ASK_FOR_HELP_ANSWERED',
  TRANSFERED = 'TRANSFERED',
  DEDICATED_AGENT_UNREAD = 'DEDICATED_AGENT_UNREAD',
  DEDICATED_AGENT_UNASSIGNED = 'DEDICATED_AGENT_UNASSIGNED',
  RE_OPENED = 'RE_OPENED',
  EMPTY = 'EMPTY',
  SENT = 'SENT',
  DRAFT = 'DRAFT',
  DELEGATED = 'DELEGATED',
}

export interface IEpticaFrame {
  recipientsValidity$: Observable<boolean>;
  replyAllRecipients$: Observable<RecipientFields>;
  removed$: Observable<void>;
  getFrameId(): string;

  getId(): number;

  hide(): void;

  show(container?: HTMLElement, absolutePosition?: boolean): void;

  doInEpticaFrame(fn: (frame: HTMLIFrameElement) => void): void;

  getInEpticaFrame<T>(fn: (frame: HTMLIFrameElement) => T, defaultValue: T): T;

  finish(): void;

  customizeAnswerDisplay(): void;

  removeLocked(): void;

  isLocked(): boolean;

  addUploadFiles(files: File[]): void;

  preRemoveRequest();

  preAddRequest();

  getRecipientFields(): RecipientFields;

  setRecipientFields(recipients: RecipientFields): void;
}

export interface IEpticaRequestFrame extends IEpticaFrame {
  requestDto$: Observable<EpticaExposedRequest>;
  customerDto$: Observable<EpticaExposedCustomer>;
  closeComment: string | undefined;

  readonly updateRecipients$: Observable<UpdateRecipientFields>;

  close(comment?: string): IPromise<any>;

  askForReview(): IPromise<IEpticaJsonResponse>;

  assist(epticaRequestId: number): IPromise<IEpticaJsonResponse>;

  bounce(responseId: string): IPromise<any>;

  cancelAskForReview(): boolean;

  cancelKnowlegdeDialog(): boolean;

  cancelKnowlegdeDialogVanilla(): boolean;

  cancelAssist(): boolean;

  acceptApproval(): IPromise<IEpticaJsonResponse>;

  rejectApproval(): IPromise<IEpticaJsonResponse>;

  cancelRejectApproval(): boolean;

  requestRejectApproval(cancelAction: () => void, validateAction: () => void): boolean;

  delegate(): IPromise<IEpticaJsonResponse>;

  sendDelegate(): IPromise<EpticaData>;

  openKnowledgeDialog(cancelAction: () => void, validateAction: () => void): boolean;

  requestAskForReview(): boolean;

  validateRequestAskForReview(comment: string): IPromise<boolean>;

  send(): IPromise<IEpticaJsonResponse>;

  confirmClosePendingAssist(confirmButtonId: string): IPromise<EpticaData>;

  confirmClosePendingAssistJson(submitForm: () => IPromise<IEpticaJsonResponse>): IPromise<IEpticaJsonResponse>;

  sendAskForHelpAnswer(epticaRequestId: number): IPromise<IEpticaJsonResponse>;

  sendDefer(): IPromise<IEpticaJsonResponse>;

  defer(): IPromise<IEpticaJsonResponse>;

  getEpticaCustomer(requestId: string): string;

  getEpticaFieldValue(fieldName: string): string;

  validateKnowlegdeDialog(): boolean;

  confirmCloseAssists(): IPromise<EpticaData>;

  fillTransferToEmail(email: string, comment: string): void;

  fillTransfertToAgent(iasId: string, comment: string): void;

  fillAssistanceToEmail(email: string, comment: string): void;

  fillAssistanceToAgent(iasId: string, comment: string): void;

  reOpen(): IPromise<EpticaData>;

  // events eptica
  retrieveEvents(...selector: EpticaSelector[]): Observable<IEpticaExctractEvent[]>;

  retrieveConversationalEvents(): Observable<IEpticaExctractEvent[]>;

  retrieveApprovalEvents(): Observable<IEpticaExctractEvent[]>;

  retrieveAskForHelpEvents(): Observable<IEpticaExctractEvent[]>;

  retrieveLastTransferEvent(): Observable<IEpticaExctractEvent | undefined>;

  retrieveSubject(): Observable<string>;

  // utility methods
  getPageName(): string;

  hasOpenedPopup(): boolean;

  addPrintAndPreviewButtons(): void;

  addPrintButtonForAssists(): void;

  addChangeRecipientButton(responseId: string, requestId: number, parentRef: any): void;

  addPJEventsForAskForHelp(callback: any): void;

  addPJEventsForDelegate(callback: any): void;

  addPJEventsForAnswer(callback: any): void;

  removePJEvents(): void;

  removePJEventsAskForHelp(): void;

  removePJEventsDelegate(): void;

  expandComments(): void;

  simulateClickOnSelection(): void;

  simulateClickOnUploadDelegate(): void;

  simulateClickOnUploadAskForHelp(): void;

  setSubjectContactStyle(): void;

  setCCContactStyle(): void;

  setBCCContactStyle(): void;

  setRecipientContactStyle(): void;

  setChannelContactStyle(): void;

  moveSubjectInHeader(): string;

  removeAttachmentFileForAnswer(file: IEpticaExctractAttachment): void;

  removeAttachmentFileForAskForHelp(file: IEpticaExctractAttachment): void;

  removeAttachmentFileForDelegate(file: IEpticaExctractAttachment): void;

  changeRequestCustomer(newIasCustomer: EpticaEnterpriseAgentExposed): void;
  addEditorActionButtons(requestId: string, buttonList: EpticaButtonActionTypes[]): void;

  addTextToEditor(txt: string, $i18next: i18n): void;

  clearDropZone(): void;

  updateRecipients(updated: UpdateRecipientFields): void;

  copyLastHistoryToEditor(
    historicFrom: string,
    historicTo: string,
    historicCc: string,
    historicSubject: string,
    historicDateTime: string,
    historicContentHtml: string,
    $i18next: i18n
  ): void;

  selectAllMessageRequestHistory(): void;

  /**
   * Used to find the selected messages to be included in the forward
   * @param {string} requestId the request id to use
   * @returns {string[]} the messages id to include
   */
  getIncludedMessages(requestId: string): string[];

  overrideClosePendingAssist(confirmAction: () => IPromise<EpticaData>): void;

  overrideClosePendingAssistJson(confirmAction: () => IPromise<IEpticaJsonResponse>): void;

  isOnPage(pagename: string): boolean;

  addCustomClass(classStr: string);

  addClassToEditorFormContainer();

  uniformUploadButtonForAskForHelp(): void;

  printMessageClientWithHtmlFormat(): void;

  preview();

  print();

  appearRequestAnswerAssistance(): boolean;

  showElement(selectorName: string): void;

  hideElement(selectorName: string): void;

  clearDraftAction(): void;
  getAllRecipients(blackListMailAddress: string): string[];
  replyAllAction(blackListMailAddress: string): void;
  toggleDelegatePanel(show: boolean): boolean;

  toggleAnswerPanel(show: boolean): boolean;

  toggleClosePanel(show: boolean): boolean;

  toggleAssistPanel(show: boolean): boolean;

  toggleConversationalMode(): void;

  toggleRecipientAddress(show: boolean);

  hideAttachmentInAnswerZone();

  saveDraft(): IPromise<EpticaData>;

  loadDraftAttachments(callback: any): void;

  isMessageTextEmpty(): boolean;
  addCcAndCccContacts(): void;

  isValidForm(): boolean;
  switchCommentFormat(elemId: string): void;
  checkFirstExistingSelector(...selector): string;

  toggleMailHistoryCheckBox(elemId: string, checked: boolean): void;
}

export interface IEpticaOutgoingFrame extends IEpticaFrame {
  isMessageTextEmpty(): boolean;
  send(): IPromise<IEpticaJsonResponse>;

  sendPostpone(): IPromise<IEpticaJsonResponse>;
  defer(): IPromise<IEpticaJsonResponse>;
  browseKnowledge(cancelAction: () => void, validateAction: () => void): boolean;

  validateKnowlegdeDialog(): boolean;

  cancelKnowlegdeDialog(): boolean;

  cancelDraft(): IPromise<EpticaData>;

  hideRecipient(): void;

  customizeAnswerDisplay(): void;

  saveDraft(): IPromise<EpticaData>;

  /** @Deprecated */ addPrintAndPreviewButtons(): void;

  preview(): void;

  focus(): void;

  print(): void;

  setOutgoingClass(): void;

  getRealId(): Option<number>;

  setRealId(realId: number): void;

  changeIncomingChannelLabel(): void;

  addCcAndCccContacts(): void;
}

export interface MailRequestResult {
  total: number;
  data: MailRequest[];
}

export interface MailRequest {
  id: string;
  detail: EpticaExposedRequestForDraft;
  customer: EpticaExposedCustomer | undefined;
  askForHelpStatus: {
    status: 'OPEN' | 'ANSWERED' | undefined;
    responsible: string | undefined;
  };
}

export abstract class EpticaPage {
  private locked: boolean;

  private _recipientsValidity$ = new BehaviorSubject<boolean>(false);
  readonly recipientsValidity$ = this._recipientsValidity$.pipe(distinctUntilChanged());

  protected _replyAllRecipients$ = new Subject<any>(); // we do not need initial value at subscribe, so it a simple Subject
  readonly replyAllRecipients$ = this._replyAllRecipients$.pipe(distinctUntilChanged());

  private _removed$ = new Subject<boolean>(); // we do not need initial value at subscribe, so it a simple Subject
  readonly removed$ = this._removed$.pipe(fTruthy());

  protected recipientMethods = {
    to: {
      get: EpticaHelper.getToRecipients.bind(EpticaHelper),
      set: EpticaHelper.setToRecipients.bind(EpticaHelper),
    },
    cc: {
      get: EpticaHelper.getCcRecipients.bind(EpticaHelper),
      set: EpticaHelper.setCcRecipients.bind(EpticaHelper),
    },
    bcc: {
      get: EpticaHelper.getBccRecipients.bind(EpticaHelper),
      set: EpticaHelper.setBccRecipients.bind(EpticaHelper),
    },
  };

  constructor(
    protected readonly frameId: string,
    protected readonly extApp: AppExternalService,
    protected readonly toastDeprecatedService: ToastDeprecatedService,
    protected readonly epticaDeprecatedService: IEpticaDeprecatedService,
    protected readonly $log: ILogService
  ) {
    this.locked = false;
  }

  getFrameId(): string {
    return this.frameId;
  }

  hide(): void {
    this.extApp.hideApplication(this.frameId);
  }

  finish() {
    this.hide();
    this.removeLocked();
    this.internalFinish();
  }

  doInEpticaFrame(fn: (frame: HTMLIFrameElement) => void): void {
    this.extApp.getApplicationContainer(this.frameId).map(frame => fn(frame));
  }

  getInEpticaFrame<T>(fn: (frame: HTMLIFrameElement) => T, defaultValue: T): T {
    return this.extApp
      .getApplicationContainer(this.frameId)
      .map(frame => fn(frame))
      .getOrElse(defaultValue);
  }

  show(container?: HTMLElement, absolutePosition: boolean = true): void {
    this.locked = true;
    Option.of(container).forEach(element => {
      if (absolutePosition) {
        this.extApp.positionApplication(this.frameId, element);
      } else {
        this.extApp.insertApplication(this.frameId, element);
      }
    });
    this.extApp.showApplication(this.frameId);
  }

  showMessages(): void {
    this.extApp.getApplicationContainer(this.frameId).map(frame => {
      EpticaHelper.displayActionMessages(this.toastDeprecatedService, frame);
    });
  }

  removeLocked() {
    this.locked = false;
  }

  isLocked(): boolean {
    return this.locked;
  }

  isMessageTextEmpty(): boolean {
    return this.getInEpticaFrame(frame => EpticaHelper.isMessageTextEmpty(frame), true);
  }

  setRecipientFields(recipients: RecipientFields): void {
    this.doInEpticaFrame(frame => {
      if (recipients.to) this.recipientMethods.to.set(frame, recipients.to);
      if (recipients.cc) this.recipientMethods.cc.set(frame, recipients.cc);
      if (recipients.bcc) this.recipientMethods.bcc.set(frame, recipients.bcc);
    });
    this._recipientsValidity$.next(!!recipients.valid);
  }

  getRecipientFields(): { to: string[]; cc: string[]; bcc: string[] } {
    return this.getInEpticaFrame(
      frame => {
        let toContacts: string[] = this.recipientMethods.to.get(frame);
        // the "to" should be the last contact that was addressed, but if there's an ongoing draft, we keep whatever is in the draft
        if (!EpticaHelper.callEpticaFn<boolean>(frame, undefined, 'requestHasDrafts').getOrElse(false)) {
          EpticaHelper.getEpticaData(frame).map(data =>
            data.pageContext.request
              .flatMap(request => request.lastContact)
              .map(lastContact => {
                toContacts = (lastContact && lastContact.match(EMAIL_LIST_REGEXP)) || toContacts;
              })
          );
        }
        return {
          // get unique toRecipients to avoid returning a mail address twice
          to: toContacts.filter((elem, index, self) => index === self.indexOf(elem)) || [],
          cc: this.recipientMethods.cc.get(frame) || [],
          bcc: this.recipientMethods.bcc.get(frame) || [],
        };
      },
      {
        to: [],
        cc: [],
        bcc: [],
      }
    );
  }

  protected abstract internalFinish();

  protected abstract getDirection(): ChannelDirection;

  protected createCcAndBccContacts(frame: HTMLIFrameElement): void {
    try {
      const emails = EpticaHelper.getCcAndBccValues(frame, this.getDirection());
      if (emails && emails.length > 0) {
        this.epticaDeprecatedService.createContactsFromEmails(emails).subscribe();
      }
    } catch (e) {
      this.$log.error(e);
    }
  }

  preRemoveRequest() {
    this._removed$.next(true);
    this._removed$.complete();
    this._removed$.unsubscribe();
  }
}

export interface IEpticaJsonResponse {
  messageState: string;
  messageLabel: string;
}

export interface IEpticaJsonResponseGlobal {
  messageState: string;
  newToken?: any;
  messageLabel: string;
}
